<template>
    <div
        class="ppv2-w-full ppv2-flex ppv2-flex-col ppv2-gap-4 ppv2-bg-white dark:ppv2-bg-dark"
        style="border-top: 1px solid #eef1f5; padding: 18px 18px 20px"
    >
        <div class="ppv2-grid" style="grid-template-columns: 1fr 260px 260px; gap: 16px; align-items: start">
            <label class="ppv2-flex ppv2-flex-col ppv2-gap-1.5">
                <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(12.5), color: '#444c57' }">
                    {{ $t('forms.question.intitule') }}
                    <span class="ppv2-text-[#7d2dbd]">*</span>
                </span>
                <CustomInput
                    :value="question.label"
                    :placeholder="$t('forms.question.placeholder')"
                    class-input-add="!ppv2-h-[40px] !ppv2-rounded-[9px] !ppv2-border-[#cbd2dc] !ppv2-px-3"
                    class-container-add="ppv2-w-full !ppv2-mb-0"
                    :custom-size-label="14"
                    :disabled="question.locked"
                    @input="question.label = $event"
                />
            </label>
            <label class="ppv2-flex ppv2-flex-col ppv2-gap-1.5">
                <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(12.5), color: '#444c57' }">
                    {{ $t('forms.question.typeLabel') }}
                    <span class="ppv2-text-[#7d2dbd]">*</span>
                </span>
                <BaseSelect
                    :value="question.typeKey"
                    :options="typeOptions"
                    option-label="label"
                    option-value="key"
                    :searchable="false"
                    :custom-size-label="14"
                    :custom-input-class="'!ppv2-h-[40px]'"
                    :class-container-added="'ppv2-w-full !ppv2-mb-0'"
                    :disabled="question.locked"
                    fixed-dropdown
                    @input="onTypeChange"
                />
            </label>
            <label class="ppv2-flex ppv2-flex-col ppv2-gap-1.5">
                <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(12.5), color: '#444c57' }">
                    {{ $t('forms.question.placeholderLabel') }}
                </span>
                <CustomInput
                    :value="question.placeholder"
                    :placeholder="$t('forms.question.placeholderHint')"
                    class-input-add="!ppv2-h-[40px] !ppv2-rounded-[9px] !ppv2-border-[#cbd2dc] !ppv2-px-3"
                    class-container-add="ppv2-w-full !ppv2-mb-0"
                    :custom-size-label="14"
                    :disabled="question.locked"
                    @input="question.placeholder = $event"
                />
            </label>
        </div>

        <template v-if="hasOptions">
            <div
                class="ppv2-flex ppv2-flex-col ppv2-gap-2"
                style="padding: 14px; background: #fff; border: 1px solid #eef1f5; border-radius: 10px"
            >
                <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-3">
                    <span class="poppinsSemiBold" :style="{ fontSize: normalizeFontSize(12.5), color: '#021832' }">
                        {{ $t('forms.question.optionsTitle') }}
                    </span>
                    <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(12), color: '#9aa4b2' }">
                        {{ optionCount }}
                    </span>
                </div>
                <div v-for="(option, i) in question.options" :key="i" class="ppv2-flex ppv2-items-center ppv2-gap-2.5">
                    <span class="ppv2-shrink-0 ppv2-w-[18px] ppv2-h-[18px]" :style="markStyle"></span>
                    <input
                        v-model="question.options[i]"
                        type="text"
                        class="ppv2-flex-1 ppv2-min-w-0 ppv2-h-9 ppv2-px-3 ppv2-outline-none ppv2-rounded-lg"
                        style="border: 1px solid #e2e6ec; background: #fff"
                        :placeholder="$t('forms.question.optionPlaceholder', { n: i + 1 })"
                        :style="{ fontSize: normalizeFontSize(13.5), color: '#021832' }"
                    />
                    <button
                        type="button"
                        class="ppv2-shrink-0 ppv2-w-8 ppv2-h-8 ppv2-rounded-lg ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-[#fdeceb] hover:ppv2-text-[#c0392b] ppv2-transition-colors"
                        :title="$t('forms.question.delete')"
                        @click="removeOption(i)"
                    >
                        <FormTypeIcon name="minus" :size="15" />
                    </button>
                </div>
                <button
                    type="button"
                    class="ppv2-self-start ppv2-inline-flex ppv2-items-center ppv2-gap-[7px] ppv2-h-[34px] ppv2-mt-0.5 ppv2-px-3 ppv2-rounded-lg ppv2-border ppv2-border-dashed ppv2-text-[#6b7480] hover:ppv2-border-[#7d2dbd] hover:ppv2-text-[#7d2dbd] hover:ppv2-bg-[#faf6fe] poppinsMedium ppv2-transition-colors"
                    style="border-color: #cbd2dc; background: transparent"
                    :style="{ fontSize: normalizeFontSize(13) }"
                    @click="addOption"
                >
                    <FormTypeIcon name="plus" :size="15" />
                    {{ $t('forms.question.addOption') }}
                </button>
            </div>
        </template>

        <template v-if="question.typeKey === 'experience'">
            <div
                class="ppv2-flex ppv2-flex-col ppv2-gap-2"
                style="padding: 14px; background: #fff; border: 1px solid #eef1f5; border-radius: 10px"
            >
                <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-3">
                    <span class="poppinsSemiBold" :style="{ fontSize: normalizeFontSize(12.5), color: '#021832' }">
                        {{ $t('forms.question.experiencesTitle') }}
                    </span>
                    <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(12), color: '#9aa4b2' }">
                        {{ (question.options || []).length }} / {{ experiences.length }}
                    </span>
                </div>
                <p class="poppinsRegular" :style="{ fontSize: normalizeFontSize(12), color: '#9aa4b2' }">
                    {{ $t('forms.question.experiencesHint') }}
                </p>
                <div v-if="experiencesLoading" class="poppinsRegular" :style="{ fontSize: normalizeFontSize(13) }">
                    {{ $t('forms.question.experiencesLoading') }}
                </div>
                <div
                    v-else-if="experiencesError"
                    class="poppinsRegular"
                    :style="{ fontSize: normalizeFontSize(13), color: '#e2483d' }"
                >
                    {{ experiencesError }}
                    <button
                        type="button"
                        class="poppinsMedium ppv2-ml-2 ppv2-underline"
                        :style="{ fontSize: normalizeFontSize(13), color: '#7d2dbd' }"
                        @click="fetchExperiences"
                    >
                        {{ $t('forms.question.experiencesRetry') }}
                    </button>
                </div>
                <div
                    v-else-if="!experiences.length"
                    class="poppinsRegular"
                    :style="{ fontSize: normalizeFontSize(13), color: '#9aa4b2' }"
                >
                    {{ $t('forms.question.experiencesEmpty') }}
                </div>
                <template v-else>
                    <div class="ppv2-relative">
                        <input
                            v-model="experienceSearch"
                            type="text"
                            class="ppv2-w-full ppv2-h-9 ppv2-pl-9 ppv2-pr-8 ppv2-outline-none ppv2-rounded-lg"
                            style="border: 1px solid #cbd2dc; background: #fff"
                            :style="{ fontSize: normalizeFontSize(13.5), color: '#021832' }"
                            :placeholder="$t('forms.question.experiencesSearch')"
                        />
                        <span
                            class="ppv2-absolute ppv2-left-3 ppv2-top-1/2 ppv2-pointer-events-none"
                            style="transform: translateY(-50%); color: #9aa4b2"
                        >
                            <FormTypeIcon name="search" :size="15" />
                        </span>
                        <button
                            v-if="experienceSearch"
                            type="button"
                            class="ppv2-absolute ppv2-right-2 ppv2-top-1/2 ppv2-w-6 ppv2-h-6 ppv2-rounded-md ppv2-flex ppv2-items-center ppv2-justify-center hover:ppv2-bg-black hover:ppv2-bg-opacity-5"
                            style="transform: translateY(-50%); color: #9aa4b2"
                            :title="$t('forms.question.experiencesClearSearch')"
                            @click="experienceSearch = ''"
                        >
                            <FormTypeIcon name="close" :size="13" />
                        </button>
                    </div>
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                        <button
                            type="button"
                            class="poppinsMedium ppv2-underline"
                            :style="{ fontSize: normalizeFontSize(12.5), color: '#7d2dbd' }"
                            @click="selectAllExperiences"
                        >
                            {{ $t('forms.question.experiencesSelectAll') }}
                        </button>
                        <span :style="{ fontSize: normalizeFontSize(12.5), color: '#cbd2dc' }">·</span>
                        <button
                            type="button"
                            class="poppinsMedium ppv2-underline"
                            :style="{ fontSize: normalizeFontSize(12.5), color: '#6b7480' }"
                            @click="clearExperiences"
                        >
                            {{ $t('forms.question.experiencesClear') }}
                        </button>
                        <span
                            v-if="experienceSearch"
                            class="poppinsRegular ppv2-ml-auto"
                            :style="{ fontSize: normalizeFontSize(12), color: '#9aa4b2' }"
                        >
                            {{ filteredExperiences.length }} / {{ experiences.length }}
                        </span>
                    </div>
                    <div
                        v-if="!filteredExperiences.length"
                        class="poppinsRegular"
                        :style="{ fontSize: normalizeFontSize(13), color: '#9aa4b2' }"
                    >
                        {{ $t('forms.question.experiencesNoResult') }}
                    </div>
                    <div
                        v-else
                        class="ppv2-flex ppv2-flex-col ppv2-gap-2 ppv2-overflow-y-auto ppv2-pr-1"
                        style="max-height: 260px"
                    >
                        <label
                            v-for="name in filteredExperiences"
                            :key="name"
                            class="ppv2-flex ppv2-items-center ppv2-gap-2.5 ppv2-px-3 ppv2-py-2 ppv2-rounded-lg ppv2-cursor-pointer hover:ppv2-bg-black hover:ppv2-bg-opacity-5"
                            style="border: 1px solid #eef1f5; cursor: pointer"
                        >
                            <input
                                type="checkbox"
                                :checked="isExperienceSelected(name)"
                                style="width: 15px; height: 15px; accent-color: #7d2dbd"
                                @change="toggleExperience(name)"
                            />
                            <span class="poppinsRegular ppv2-flex-1 ppv2-truncate" :style="{ fontSize: normalizeFontSize(13.5) }">
                                {{ name }}
                            </span>
                        </label>
                    </div>
                </template>
            </div>
        </template>

        <template v-if="question.typeKey === 'checkbox'">
            <div class="ppv2-flex ppv2-items-center ppv2-gap-3" :style="{ fontSize: normalizeFontSize(13), color: '#444c57' }">
                <span>{{ $t('forms.question.minChoicesPrefix') }}</span>
                <span
                    class="ppv2-inline-flex ppv2-items-center"
                    style="border: 1px solid #cbd2dc; border-radius: 8px; overflow: hidden; height: 34px"
                >
                    <button
                        type="button"
                        class="ppv2-h-full ppv2-bg-black ppv2-bg-opacity-[3%] hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-transition-colors"
                        style="width: 32px; border: none; color: #444c57; cursor: pointer"
                        :style="{ fontSize: normalizeFontSize(15) }"
                        @click="decMinChoices"
                    >
                        −
                    </button>
                    <span
                        class="ppv2-select-none"
                        style="width: 38px; text-align: center; font-weight: 600; color: #021832"
                        :style="{ fontSize: normalizeFontSize(13.5) }"
                    >
                        {{ question.minChoices }}
                    </span>
                    <button
                        type="button"
                        class="ppv2-h-full ppv2-bg-black ppv2-bg-opacity-[3%] hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-transition-colors"
                        style="width: 32px; border: none; color: #444c57; cursor: pointer"
                        :style="{ fontSize: normalizeFontSize(15) }"
                        @click="incMinChoices"
                    >
                        +
                    </button>
                </span>
                <span>{{ minChoicesUnit }}</span>
            </div>
        </template>

        <template v-if="question.typeKey === 'number'">
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-1">
                <div
                    class="ppv2-flex ppv2-items-center ppv2-gap-3"
                    :style="{ fontSize: normalizeFontSize(13), color: '#444c57' }"
                >
                    <span>
                        {{ $t('forms.question.numberBetween') }}
                        <span v-if="question.typeKey !== 'number'" class="ppv2-text-[#7d2dbd]">*</span>
                    </span>
                    <input
                        v-model="question.min"
                        type="number"
                        class="ppv2-outline-none ppv2-text-center"
                        style="width: 80px; height: 34px; padding: 0 10px; border: 1px solid #cbd2dc; border-radius: 8px"
                        :style="{ fontSize: normalizeFontSize(13.5), borderColor: question.boundsError ? '#e2483d' : undefined }"
                        @input="question.boundsError = false"
                    />
                    <span>{{ $t('forms.question.and') }}</span>
                    <input
                        v-model="question.max"
                        type="number"
                        class="ppv2-outline-none ppv2-text-center"
                        style="width: 80px; height: 34px; padding: 0 10px; border: 1px solid #cbd2dc; border-radius: 8px"
                        :style="{ fontSize: normalizeFontSize(13.5), borderColor: question.boundsError ? '#e2483d' : undefined }"
                        @input="question.boundsError = false"
                    />
                </div>
                <p
                    v-if="question.boundsError"
                    class="poppinsRegular ppv2-mt-1 ppv2-pl-1"
                    :style="{ fontSize: normalizeFontSize(12), color: '#e2483d' }"
                >
                    {{ $t('forms.question.boundsRequired') }}
                </p>
            </div>
        </template>

        <template v-if="question.typeKey === 'long'">
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-1">
                <div
                    class="ppv2-flex ppv2-items-center ppv2-gap-3"
                    :style="{ fontSize: normalizeFontSize(13), color: '#444c57' }"
                >
                    <span>
                        {{ $t('forms.question.maxLengthLabel') }}
                        <span v-if="question.typeKey !== 'long'" class="ppv2-text-[#7d2dbd]">*</span>
                    </span>
                    <input
                        v-model="question.max"
                        type="number"
                        class="ppv2-outline-none ppv2-text-center"
                        style="width: 90px; height: 34px; padding: 0 10px; border: 1px solid #cbd2dc; border-radius: 8px"
                        :style="{ fontSize: normalizeFontSize(13.5), borderColor: question.boundsError ? '#e2483d' : undefined }"
                        @input="question.boundsError = false"
                    />
                    <span>{{ maxLengthUnit }}</span>
                </div>
                <p
                    v-if="question.boundsError"
                    class="poppinsRegular ppv2-mt-1 ppv2-pl-1"
                    :style="{ fontSize: normalizeFontSize(12), color: '#e2483d' }"
                >
                    {{ $t('forms.question.maxLengthRequired') }}
                </p>
            </div>
        </template>

        <template v-if="question.typeKey === 'date'">
            <div>
                <div class="ppv2-flex ppv2-items-center ppv2-flex-wrap ppv2-gap-5">
                    <span class="ppv2-flex ppv2-items-center ppv2-gap-2">
                        <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(13) }">
                            {{ $t('forms.question.dateFormatLabel') }}
                        </span>
                        <span class="ppv2-inline-flex ppv2-p-[3px] ppv2-rounded-lg ppv2-gap-0.5" style="background: #eef1f5">
                            <button
                                type="button"
                                class="poppinsRegular ppv2-rounded-md ppv2-px-3 ppv2-py-[5px] ppv2-transition-colors"
                                :style="dateSegStyle('single')"
                                @click="question.dateFormat = 'single'"
                            >
                                {{ $t('forms.question.dateSingle') }}
                            </button>
                            <button
                                type="button"
                                class="poppinsRegular ppv2-rounded-md ppv2-px-3 ppv2-py-[5px] ppv2-transition-colors"
                                :style="dateSegStyle('range')"
                                @click="question.dateFormat = 'range'"
                            >
                                {{ $t('forms.question.dateRange') }}
                            </button>
                        </span>
                    </span>
                    <label class="ppv2-flex ppv2-items-center ppv2-gap-2" style="cursor: pointer">
                        <input
                            type="checkbox"
                            :checked="question.forbidPast"
                            @change="question.forbidPast = $event.target.checked"
                            style="width: 15px; height: 15px; accent-color: #7d2dbd"
                        />
                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(13) }">
                            {{ $t('forms.question.forbidPast') }}
                        </span>
                    </label>
                </div>
            </div>
        </template>

        <div
            class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-4"
            style="padding-top: 14px; border-top: 1px solid #eef1f5"
        >
            <div class="ppv2-flex ppv2-items-center ppv2-gap-2.5 ppv2-cursor-pointer ppv2-select-none" @click="toggleRequired">
                <div @click.stop>
                    <BaseSwitch
                        :model-value="question.required"
                        variant="purple"
                        :disabled="question.locked"
                        @input="question.required = $event"
                    />
                </div>
                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(13), color: '#021832' }">
                    {{ $t('forms.question.requiredAnswer') }}
                </span>
            </div>
            <BaseButton
                type="button"
                variant="outline"
                custom-height="34"
                custom-font-size="13"
                custom-class="poppinsMedium !ppv2-min-w-0 !ppv2-px-4 !ppv2-py-0 !ppv2-border !ppv2-border-[#e2e6ec] ppv2-text-black hover:!ppv2-bg-black hover:!ppv2-bg-opacity-5 !ppv2-rounded-[8px]"
                @click="$emit('close')"
            >
                {{ $t('forms.question.collapse') }}
            </BaseButton>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { plural } from '../../../../utils/plural'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import FormTypeIcon from './FormTypeIcon.vue'
import { FORM_TYPES } from '../formTypes'

export default {
    name: 'FormQuestionEditor',
    components: { CustomInput, BaseSelect, BaseSwitch, BaseButton, FormTypeIcon },
    props: {
        question: {
            type: Object,
            required: true,
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            experiences: [],
            experiencesLoading: false,
            experiencesError: '',
            experienceSearch: '',
        }
    },
    created() {
        if (this.question.typeKey === 'experience') {
            this.fetchExperiences()
        }
    },
    computed: {
        hasOptions() {
            return ['radio', 'checkbox', 'select'].includes(this.question.typeKey)
        },
        typeOptions() {
            return Object.keys(FORM_TYPES).map((key) => ({ key, label: this.$t(FORM_TYPES[key].label) }))
        },
        optionCount() {
            const n = this.question.options.length
            return this.$t('forms.question.summary.answers', { n, plural: plural(n, '', 's') })
        },
        minChoicesUnit() {
            return plural(this.question.minChoices, this.$t('forms.question.answerUnit'))
        },
        maxLengthUnit() {
            return plural(Number(this.question.max) || 0, this.$t('forms.question.characterUnit'))
        },
        markStyle() {
            const radius = this.question.typeKey === 'radio' ? '50%' : '4px'
            return { borderRadius: radius, border: '1.5px solid #cbd2dc' }
        },
        filteredExperiences() {
            const query = (this.experienceSearch || '')
                .trim()
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
            if (!query) return this.experiences
            return this.experiences.filter((name) =>
                String(name || '')
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .includes(query)
            )
        },
    },
    methods: {
        normalizeFontSize,
        addOption() {
            this.question.options.push('')
        },
        removeOption(index) {
            this.question.options.splice(index, 1)
        },
        toggleRequired() {
            if (this.question.locked) return
            this.question.required = !this.question.required
        },
        incMinChoices() {
            this.question.minChoices = (Number(this.question.minChoices) || 1) + 1
        },
        decMinChoices() {
            this.question.minChoices = Math.max(1, (Number(this.question.minChoices) || 1) - 1)
        },
        onTypeChange(key) {
            const typeKey = key || 'short'
            this.question.typeKey = typeKey
            this.question.boundsError = false
            const needsOpts = ['radio', 'checkbox', 'select'].includes(typeKey)
            if (needsOpts && (!Array.isArray(this.question.options) || this.question.options.length === 0)) {
                this.question.options = [
                    this.$t('forms.question.optionPlaceholder', { n: 1 }),
                    this.$t('forms.question.optionPlaceholder', { n: 2 }),
                ]
            }
            if (typeKey === 'experience') {
                this.question.options = []
                this.experiences = []
                this.experienceSearch = ''
                this.fetchExperiences()
            }
            if (typeKey === 'date' && !this.question.dateFormat) {
                this.question.dateFormat = 'single'
            }
        },
        async fetchExperiences() {
            if (this.experiencesLoading || this.experiences.length) return
            this.experiencesLoading = true
            this.experiencesError = ''
            try {
                const response = await axios.get('/api/v3/formQuestion/experiences', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.experiences = (response.data?.data || []).map((exp) => String(exp.name)).filter(Boolean)
                // Par défaut, toutes les expériences peuvent être sélectionnées :
                // une question sans sélection propose l'ensemble du catalogue.
                if (this.experiences.length && (!Array.isArray(this.question.options) || this.question.options.length === 0)) {
                    this.question.options = [...this.experiences]
                }
            } catch (error) {
                this.experiencesError = error.response?.data?.message || error.message
            } finally {
                this.experiencesLoading = false
            }
        },
        toggleExperience(name) {
            const options = Array.isArray(this.question.options) ? [...this.question.options] : []
            const index = options.indexOf(name)
            if (index === -1) {
                options.push(name)
            } else {
                options.splice(index, 1)
            }
            this.question.options = options
        },
        selectAllExperiences() {
            const visible =
                this.filteredExperiences && this.filteredExperiences.length ? this.filteredExperiences : this.experiences
            const selected = new Set(Array.isArray(this.question.options) ? this.question.options : [])
            visible.forEach((name) => selected.add(name))
            this.question.options = [...selected]
        },
        clearExperiences() {
            if (this.experienceSearch) {
                const visible = new Set(this.filteredExperiences)
                this.question.options = (Array.isArray(this.question.options) ? this.question.options : []).filter(
                    (name) => !visible.has(name)
                )
                return
            }
            this.question.options = []
        },
        isExperienceSelected(name) {
            return Array.isArray(this.question.options) && this.question.options.indexOf(name) !== -1
        },
        dateSegStyle(mode) {
            const active = this.question.dateFormat === mode
            return {
                background: active ? '#fff' : 'transparent',
                color: active ? '#7d2dbd' : '#6b7480',
                fontSize: normalizeFontSize(12.5),
            }
        },
    },
}
</script>
